import { useMemo } from 'react'
import { BuyDownloadButtonCSV, BuyDownloadButtonPDF, BuyDownloadPdfTemplateButton, BuyDownloadTags, BuyPdfPreview, BuySupplierPdfPreview } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderBuyLayout/BuyButtons'
import { OrderStepButtonsBase, OrderStepButtonsBaseComponentProps } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent/types'
import useTwinTranslation from '../../../../../../../../../../utils/hooks/useTwinTranslation'
import { BuyDeliveryNoteDuplicateButton } from '../../../BuyDeliveryNoteButtons'
import { OrderDivideIntoChaptersButton, OrderProductTypesButton, OrderVariablesButton } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtons'
import { OrderStepButtonsDefaultBaseComponent, OrderStepButtonsNewStyleBaseComponent } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent'

const BuyDeliveryNoteFinishedStepButtons: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    return (
        <OrderStepButtonsDefaultBaseComponent {...{ data, onSubmit, hookFunction: useBuyDeliveryNoteFinishedStepButtonsLogic, ...rest }} className='mr-8' />
    )
}

const useBuyDeliveryNoteFinishedStepButtonsLogic = ({ data, onSubmit, isReadOnly, userPermissions, orderHeaderURLs, setCompleteData, updateHeaderData }: OrderStepButtonsBaseComponentProps) => {
    const { t } = useTwinTranslation()
    const Order = data?.header
    const name = 'buy_delivery_note_finished_step_buttons'

    const defaultButtons = [{ "id": "previewBuyDeliveryNotePDF" }, { "id": "downloadBuyDeliveryNote" }, { "id": "duplicateBuyDeliveryNote" }]

    const buyDeliveryNoteDuplicateText = t('duplicateBuyDeliveryNote', 'Duplicar albarán de compra')
    const buyDeliveryNoteDownloadCSVText = t('downloadCsv', 'Descargar csv')
    const buyDeliveryNoteDownloadTagsText = t('printTags', 'Imprimir etiquetas')
    const buyDeliveryNotePreviewPDFText = t('previewPdfTemplate', 'Previsualizar plantilla PDF')
    const buyDeliveryNoteDownloadPDFTemplateText = t('downloadPdfTemplate', 'Descargar plantilla PDF')
    const buyDeliveryNoteDownloadPDFText = t('downloadPdf', 'Descargar PDF')
    const buyDeliveryNoteDivideIntoChaptersText = t('divideIntoChapters', 'Dividir en capítulos')
    const buyDeliveryNoteVariablesText = t('variables', 'Variables')
    const buyOrderProductTypesText = t('valueProductTypes', 'Valor tipos de producto')
    const buyOrderPreviewSupplierPDFText = t('previewSupplierPDF', 'Previsualizar PDF proveedor')

    const ButtonComponents: OrderStepButtonsBase = useMemo(() => {
        return {
            duplicateBuyDeliveryNote: {
                component: BuyDeliveryNoteDuplicateButton,
                extraProps: {
                    title: buyDeliveryNoteDuplicateText,
                    id: 'duplicateBuyDeliveryNote',
                    allRowData: data,
                    permissionTy: 'buy.deliveryNote',
                }
            },
            downloadBuyCsv: {
                component: BuyDownloadButtonCSV,
                extraProps: {
                    title: buyDeliveryNoteDownloadCSVText,
                    id: 'downloadBuyCsv',
                    Order,
                    typeTemplate: 'buyDeliveryNote'
                }
            },
            downloadBuyTag: {
                component: BuyDownloadTags,
                extraProps: {
                    title: buyDeliveryNoteDownloadTagsText,
                    id: 'downloadBuyTag',
                    Order,
                    typeTemplate: 'buyDeliveryNote'
                }
            },
            previewBuySupplierPDF: {
                component: BuySupplierPdfPreview,
                showButton: Order.supplierPdf?.length,
                extraProps: {
                    title: buyOrderPreviewSupplierPDFText,
                    id: 'previewBuyOrderPDFSupplier',
                    Order,
                    typeTemplate: 'buyOrder',
                    key: 'previewBuyOrderPDFSupplier'
                }
            },
            previewBuyDeliveryNotePDF: {
                component: BuyPdfPreview,
                extraProps: {
                    title: buyDeliveryNotePreviewPDFText,
                    id: 'previewBuyDeliveryNotePDF',
                    key: 'previewBuyDeliveryNotePDF',
                    Order,
                    typeTemplate: 'buyDeliveryNote'
                }
            },
            downloadBuyDeliveryNoteTemplate: {
                component: BuyDownloadPdfTemplateButton,
                extraProps: {
                    title: buyDeliveryNoteDownloadPDFTemplateText,
                    id: 'downloadBuyDeliveryNoteTemplate',
                    key: 'downloadBuyDeliveryNoteTemplate',
                    Order,
                    typeTemplate: 'buyDeliveryNote'
                }
            },
            downloadBuyDeliveryNote: {
                component: BuyDownloadButtonPDF,
                extraProps: {
                    title: buyDeliveryNoteDownloadPDFText,
                    id: 'downloadBuyDeliveryNote',
                    Order,
                    typeTemplate: 'buyDeliveryNote'
                }
            },
            divideIntoChaptersQuote: {
                component: OrderDivideIntoChaptersButton,
                extraProps: {
                    title: buyDeliveryNoteDivideIntoChaptersText,
                    id: 'divideIntoChaptersQuote',
                    allRowData: data,
                    onSubmit,
                    isReadOnly
                }
            },
            variablesQuote: {
                component: OrderVariablesButton,
                extraProps: {
                    title: buyDeliveryNoteVariablesText,
                    id: 'variablesQuote',
                    allRowData: data,
                    onSubmit,
                    isReadOnly
                }
            },
            buyProductTypes: {
                component: OrderProductTypesButton,
                extraProps: {
                    title: buyOrderProductTypesText,
                    id: 'buyProductTypes',
                    allRowData: data,
                    onSubmit,
                    isReadOnly
                }
            },
        }
    }, [Order, data, onSubmit, buyDeliveryNoteDivideIntoChaptersText, buyDeliveryNoteDownloadCSVText, buyDeliveryNoteDownloadPDFTemplateText, buyDeliveryNoteDownloadPDFText, buyDeliveryNoteDownloadTagsText, buyDeliveryNoteDuplicateText, buyDeliveryNotePreviewPDFText, buyDeliveryNoteVariablesText, isReadOnly, buyOrderProductTypesText, buyOrderPreviewSupplierPDFText ])

    return { name, ButtonComponents, defaultButtons }
}

export const BuyDeliveryNoteFinishedStepButtonsNew: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    return (
        <OrderStepButtonsNewStyleBaseComponent {...{ data, onSubmit, hookFunction: useBuyDeliveryNoteFinishedStepButtonsLogic, ...rest }} className='mr-8' />
    )
}

export default BuyDeliveryNoteFinishedStepButtons